import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useKYC } from '@flipster/features/src/shared/kyc';
import { launchpoolItemBunja, useLaunchpool } from '@flipster/features/src/shared/launchpool/state';
import { useUserTier } from '@flipster/features/src/shared/user-tier/state';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import segment, { WalletEvent } from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import StakingModal from '../../_modals/staking-modal';
import { getVIPEstimatedApr } from '../utils';

export default function useStakeFlow() {
  const router = useRouter();
  const overlay = useOverlay();

  const { launchpoolItemAtom } = useBunja(launchpoolItemBunja);
  const launchpool = useAtomValue(launchpoolItemAtom);
  const { isLoggedIn, isValidKyc } = useAuth();
  const { isAchievedLevel, kycInfo } = useKYC();
  const { userTier } = useUserTier();
  const { launchpoolList } = useLaunchpool();

  return function startStake() {
    segment.track(StakeEvent.StakeButtonClick, {
      symbol: launchpool.stakingCurrency,
      apr: launchpool.estimatedApr,
      reward_amount: launchpool.estimatedReward,
      total_quantity: launchpool.totalStaked,
      my_quantity: launchpool.stakedAmount,
      apr_vip: getVIPEstimatedApr(launchpool.estimatedApr, launchpool.vipMultiplier),
      vip_status: userTier?.status,
    });

    if (!isLoggedIn) {
      router.push('/signin');
      return;
    }

    if (!isValidKyc) {
      dialog.open({
        title: {
          label: 'Verification required',
        },
        content: 'Please verify your identity to withdraw currencies.',
        buttons: {
          primary: (
            <Dialog.Button
              hierarchy='primary'
              label='Verify'
              onClick={() => {
                segment.track(WalletEvent.KycVerificationRequireClick, { kyc_level: kycInfo.currentKYCLevel });
                router.push('/user/kyc');
              }}
            />
          ),
          secondary: (
            <Dialog.Button
              hierarchy='secondary'
              color='neutral'
              label='Close'
              onClick={() => {
                segment.track(WalletEvent.VerifyCancelButton);
              }}
            />
          ),
        },
      });
      return;
    }

    if (!isAchievedLevel) {
      dialog.open({
        title: {
          label: <FormattedMessage id='Assets.r250401.Identity_verification' />,
        },
        content: <FormattedMessage id='Autogen_common.r251126.s_Required_to_participate_in_this_event' />,
        buttons: {
          primary: (
            <Dialog.Button
              hierarchy='primary'
              label={<FormattedMessage id='Assets.r250401.Verify_now' />}
              onClick={() => {
                segment.track(WalletEvent.KycVerificationRequireClick, { kyc_level: kycInfo.currentKYCLevel });
                router.push('/user/kyc');
              }}
            />
          ),
          secondary: (
            <Dialog.Button
              hierarchy='secondary'
              color='neutral'
              label={<FormattedMessage id='Common.r250401.Cancel' />}
              onClick={() => {
                segment.track(WalletEvent.VerifyCancelButton);
              }}
            />
          ),
        },
      });
      return;
    }

    if (launchpoolList?.isAbuserOrInternal) {
      dialog.open({
        title: {
          label: 'Staking unavailable',
        },
        content: 'Due to policy restrictions, staking is unavailable.',
        buttons: {
          primary: <Dialog.Button hierarchy='primary' label='Close' />,
        },
      });
      return;
    }

    overlay.open(({ isOpen, close, unmount }) => (
      <StakingModal launchpoolId={launchpool.id} isOpen={isOpen} onClose={close} onExit={unmount} />
    ));
  };
}
